import { Col, InputNumber, Modal, Radio, Row, Space, Typography } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useEffect, useState } from "react";

dayjs.extend(customParseFormat);

const { Text } = Typography;

export default function TimePickerModal({
  open,
  handleOk,
  handleCancel,
  schedIndex,
  startTime,
  endTime,
}) {
  const [error, setError] = useState(null);
  const [disable, setDisable] = useState(true);
  const [time, setTime] = useState([]);
  const [tempTime, setTempTime] = useState({
    startTime: {
      hour: "",
      minute: "",
      meridiem: "",
    },
    endTime: {
      hour: "",
      minute: "",
      meridiem: "",
    },
  });
  const meridiem = [
    { label: "AM", value: "AM" },
    { label: "PM", value: "PM" },
  ];

  const formatNumber = (num) =>
    num !== null ? num.toString().padStart(2, "0") : "";
  const parseNumber = (value) => parseInt(value, 10) || 0;

  const hanldeOnOk = () => {
    handleOk(schedIndex, time);
  };

  const handleValueOnChange = (timeSlot, timePart, value) => {
    setTempTime((prev) => ({
      ...prev,
      [timeSlot]: {
        ...prev[timeSlot],
        [timePart]: value,
      },
    }));
  };

  function createValidDate(hour, minute, meridiem) {
    const today = dayjs().format("YYYY-MM-DD");

    const formattedTime = `${hour}:${minute} ${meridiem}`;

    return dayjs(`${today} ${formattedTime}`, "YYYY-MM-DD h:m A");
  }

  const handleOnCancel = () => {
    setError(null);
    setDisable(true);
    setTime([]);
    setTempTime({
      startTime: {
        hour: "",
        minute: "",
        meridiem: "",
      },
      endTime: {
        hour: "",
        minute: "",
        meridiem: "",
      },
    });
  };

  useEffect(() => {
    if (
      Object.values(tempTime.startTime).every(
        (value) => value !== null && value !== undefined,
      ) &&
      Object.values(tempTime.endTime).every(
        (value) => value !== null && value !== undefined,
      )
    ) {
      const startTimeInstance = createValidDate(
        tempTime.startTime.hour,
        tempTime.startTime.minute,
        tempTime.startTime.meridiem,
      );
      const endTimeInstance = createValidDate(
        tempTime.endTime.hour,
        tempTime.endTime.minute,
        tempTime.endTime.meridiem,
      );

      if (startTimeInstance.isValid() && endTimeInstance.isValid()) {
        if (startTimeInstance.isAfter(endTimeInstance)) {
          setDisable(true);
          setError(
            "Start time should not be later than end time. Please select a valid time range.",
          );
          return;
        } else if (endTimeInstance.diff(startTimeInstance, "minute") < 30) {
          setDisable(true);
          setError("Please set time greater than 30 minutes.");
          return;
        } else {
          setError(null);
        }

        setTime((prev) => {
          const newValue = [...prev];
          newValue[0] = `${tempTime.startTime.hour}:${formatNumber(
            tempTime.startTime.minute,
          )} ${tempTime.startTime.meridiem}`;
          newValue[1] = `${tempTime.endTime.hour}:${formatNumber(
            tempTime.endTime.minute,
          )} ${tempTime.endTime.meridiem}`;
          return newValue;
        });

        setDisable(false);
      }
    }
  }, [tempTime]);

  useEffect(() => {
    if (startTime && dayjs(startTime).isValid()) {
      setTempTime((prev) => ({
        ...prev,
        startTime: {
          hour: startTime?.format("hh"),
          minute: startTime?.format("mm"),
          meridiem: startTime?.format("A"),
        },
      }));
    }
    if (endTime && dayjs(endTime).isValid()) {
      setTempTime((prev) => ({
        ...prev,
        endTime: {
          hour: endTime?.format("hh"),
          minute: endTime?.format("mm"),
          meridiem: endTime?.format("A"),
        },
      }));
    }
  }, [startTime, endTime]);

  return (
    <Modal
      title={`Set Time for Schedule ${schedIndex + 1}`}
      open={open}
      onOk={hanldeOnOk}
      onCancel={handleCancel}
      afterClose={handleOnCancel}
      destroyOnClose={true}
      maskClosable={false}
      width="370px"
      okButtonProps={{
        disabled: disable,
        style: { width: "77.91px" },
      }}
      styles={{
        footer: {
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          marginTop: "22px",
        },
      }}
    >
      <Row gutter={[30, 10]}>
        <Col span={24}>
          <p>Start Time</p>
          <Space>
            <InputNumber
              size="large"
              min={1}
              max={12}
              placeholder="00"
              maxLength={2}
              keyboard={true}
              type="number"
              inputMode="numeric"
              defaultValue={tempTime.startTime.hour}
              formatter={formatNumber}
              parser={parseNumber}
              onChange={(value) =>
                handleValueOnChange("startTime", "hour", value)
              }
            />
            <span>:</span>
            <InputNumber
              size="large"
              min={0}
              max={59}
              placeholder="00"
              maxLength={2}
              keyboard={true}
              type="number"
              inputMode="numeric"
              defaultValue={tempTime.startTime.minute}
              formatter={formatNumber}
              parser={parseNumber}
              onChange={(value) =>
                handleValueOnChange("startTime", "minute", value)
              }
            />
            <Radio.Group
              size="large"
              buttonStyle="solid"
              optionType="button"
              options={meridiem}
              defaultValue={tempTime.startTime.meridiem}
              onChange={(elem) =>
                handleValueOnChange("startTime", "meridiem", elem.target.value)
              }
            />
          </Space>
          <p style={{ marginTop: "10px" }}>End Time</p>
          <Space>
            <InputNumber
              size="large"
              min={1}
              max={12}
              placeholder="00"
              maxLength={2}
              type="number"
              keyboard={true}
              inputMode="numeric"
              defaultValue={tempTime.endTime.hour}
              formatter={formatNumber}
              parser={parseNumber}
              onChange={(value) =>
                handleValueOnChange("endTime", "hour", value)
              }
            />
            <span>:</span>
            <InputNumber
              size="large"
              min={0}
              max={59}
              placeholder="00"
              maxLength={2}
              keyboard={true}
              type="number"
              inputMode="numeric"
              defaultValue={tempTime.endTime.minute}
              formatter={formatNumber}
              parser={parseNumber}
              onChange={(value) =>
                handleValueOnChange("endTime", "minute", value)
              }
            />
            <Radio.Group
              size="large"
              buttonStyle="solid"
              optionType="button"
              options={meridiem}
              defaultValue={tempTime.endTime.meridiem}
              onChange={(elem) =>
                handleValueOnChange("endTime", "meridiem", elem.target.value)
              }
            />
          </Space>
        </Col>

        {error && (
          <Col style={{ margin: 0 }}>
            <Text type="danger" style={{ lineHeight: "1" }}>
              {error}
            </Text>
          </Col>
        )}
      </Row>
    </Modal>
  );
}
